import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { alpha } from "@mui/material/styles";
import {
  useCreateReportScheduleMutation,
  useDeleteReportScheduleMutation,
  useGetReportScheduleMetaQuery,
  useGetReportSchedulesQuery,
  useRunReportScheduleNowMutation,
  useUpdateReportScheduleMutation,
} from "../../redux/admin/reportApi";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import SearchableDeviceSelect from "../../components/common/SearchableDeviceSelect";

const DEFAULT_EMAIL_SLOTS = ["", "", "", "", ""];
const REPORT_BATCH_SIZE = 9;

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
};

const defaultForm = {
  id: "",
  name: "",
  emails: DEFAULT_EMAIL_SLOTS,
  deviceIds: [],
  reportTypes: [],
  format: "csv",
  frequency: "daily",
  timeOfDay: "08:00",
  dayOfWeek: 1,
  dayOfMonth: 1,
  rangePreset: "yesterday",
  timezone: "Asia/Kolkata",
  isActive: true,
};

const dayOptions = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value) => {
  if (!value) return "-";
  const [hours = "00", minutes = "00"] = String(value).split(":");
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toneList = ["#22c55e", "#2563eb", "#8b5cf6", "#f97316", "#ec4899", "#14b8a6"];

const stepBadgeSx = {
  width: 28,
  height: 28,
  borderRadius: 999,
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(135deg, #4f46e5, #4338ca)",
  color: "#fff",
  fontWeight: 800,
  fontSize: 13,
  boxShadow: "0 10px 22px rgba(79,70,229,0.2)",
  flexShrink: 0,
};

const softCardSx = {
  borderRadius: "14px",
  border: "1px solid #e2e8f0",
  background: "#fff",
  boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
};

const ReportScheduler = () => {
  const editorRef = useRef(null);
  const [form, setForm] = useState(defaultForm);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [reportSearch, setReportSearch] = useState("");
  const [visibleReportCount, setVisibleReportCount] = useState(REPORT_BATCH_SIZE);
  const {
    data: meta = {},
    isLoading: metaLoading,
    isFetching: metaFetching,
    refetch: refetchMeta,
  } = useGetReportScheduleMetaQuery();
  const {
    data: schedulesResponse = { schedules: [] },
    isLoading: schedulesLoading,
    isFetching: schedulesFetching,
    refetch: refetchSchedules,
  } = useGetReportSchedulesQuery();
  const [createReportSchedule, { isLoading: creating }] = useCreateReportScheduleMutation();
  const [updateReportSchedule, { isLoading: updating }] = useUpdateReportScheduleMutation();
  const [deleteReportSchedule, { isLoading: deleting }] = useDeleteReportScheduleMutation();
  const [runReportScheduleNow, { isLoading: runningNow }] = useRunReportScheduleNowMutation();

  const schedules = useMemo(
    () => (Array.isArray(schedulesResponse?.schedules) ? schedulesResponse.schedules : []),
    [schedulesResponse]
  );

  const { data: rawAllDevices = [] } = useGetAllLiteDevicesQuery();
  const devices = useMemo(() => {
    const list = Array.isArray(rawAllDevices) ? rawAllDevices.flat(Infinity) : [];
    if (list.length) return list.filter((d) => d && (d._id || d.imei));
    return Array.isArray(meta?.devices) ? meta.devices : [];
  }, [rawAllDevices, meta]);

  const reportTypes = useMemo(
    () => (Array.isArray(meta?.reportTypes) ? meta.reportTypes : []),
    [meta?.reportTypes]
  );
  const formats = useMemo(
    () => (Array.isArray(meta?.formats) && meta.formats.length ? meta.formats : ["csv", "pdf", "xlsx"]),
    [meta?.formats]
  );
  const frequencies = useMemo(
    () =>
      Array.isArray(meta?.frequencies) && meta.frequencies.length ? meta.frequencies : ["daily", "weekly", "monthly"],
    [meta?.frequencies]
  );
  const rangePresets = useMemo(
    () =>
      Array.isArray(meta?.rangePresets) && meta.rangePresets.length
        ? meta.rangePresets
        : ["today", "yesterday", "last_7_days", "last_30_days"],
    [meta?.rangePresets]
  );

  const filteredReports = useMemo(() => {
    const query = reportSearch.trim().toLowerCase();
    if (!query) return reportTypes;
    return reportTypes.filter((report) => {
      const label = String(report?.label || "").toLowerCase();
      const value = String(report?.value || "").toLowerCase();
      return label.includes(query) || value.includes(query);
    });
  }, [reportSearch, reportTypes]);

  const visibleReports = filteredReports.slice(0, visibleReportCount);
  const hasMoreReports = filteredReports.length > visibleReportCount;

  const resetForm = () => {
    setForm({ ...defaultForm, emails: [...DEFAULT_EMAIL_SLOTS] });
    setStatus({ type: "", message: "" });
    setReportSearch("");
    setVisibleReportCount(REPORT_BATCH_SIZE);
  };

  const focusEditor = () => {
    window.requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleNewSchedule = () => {
    resetForm();
    focusEditor();
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setEmailAt = (index, value) => {
    setForm((prev) => {
      const nextEmails = [...prev.emails];
      nextEmails[index] = value;
      return { ...prev, emails: nextEmails };
    });
  };

  const handleEdit = (schedule) => {
    const nextEmails = [...DEFAULT_EMAIL_SLOTS];
    (schedule.emails || []).slice(0, 5).forEach((email, index) => {
      nextEmails[index] = email;
    });

    setForm({
      id: schedule._id,
      name: schedule.name || "",
      emails: nextEmails,
      deviceIds: (schedule.deviceIds || []).map((entry) => String(entry)),
      reportTypes: schedule.reportTypes || [],
      format: schedule.format || "csv",
      frequency: schedule.frequency || "daily",
      timeOfDay: schedule.timeOfDay || "08:00",
      dayOfWeek: Number(schedule.dayOfWeek ?? 1),
      dayOfMonth: Number(schedule.dayOfMonth ?? 1),
      rangePreset: schedule.rangePreset || "yesterday",
      timezone: schedule.timezone || "Asia/Kolkata",
      isActive: schedule.isActive !== false,
    });
    setStatus({ type: "", message: "" });
    focusEditor();
  };

  const handleSubmit = async () => {
    try {
      setStatus({ type: "", message: "" });
      const payload = {
        name: form.name,
        emails: toArray(form.emails).filter((entry) => String(entry || "").trim()),
        deviceIds: toArray(form.deviceIds),
        reportTypes: toArray(form.reportTypes),
        format: form.format,
        frequency: form.frequency,
        timeOfDay: form.timeOfDay,
        dayOfWeek: form.frequency === "weekly" ? form.dayOfWeek : null,
        dayOfMonth: form.frequency === "monthly" ? form.dayOfMonth : null,
        rangePreset: form.rangePreset,
        timezone: form.timezone,
        isActive: form.isActive,
      };

      if (form.id) {
        await updateReportSchedule({ id: form.id, ...payload }).unwrap();
        setStatus({ type: "success", message: "Report schedule updated successfully" });
      } else {
        await createReportSchedule(payload).unwrap();
        setStatus({ type: "success", message: "Report schedule created successfully" });
      }

      resetForm();
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.data?.message || error?.message || "Failed to save report schedule",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      setStatus({ type: "", message: "" });
      await deleteReportSchedule(id).unwrap();
      if (form.id === id) resetForm();
      setStatus({ type: "success", message: "Report schedule deleted" });
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.data?.message || error?.message || "Failed to delete report schedule",
      });
    }
  };

  const handleRunNow = async (id) => {
    try {
      setStatus({ type: "", message: "" });
      await runReportScheduleNow(id).unwrap();
      setStatus({ type: "success", message: "Report schedule executed" });
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.data?.message || error?.message || "Failed to run report schedule",
      });
    }
  };

  const busy = creating || updating || deleting || runningNow;

  const renderStepHeader = (step, title, subtitle, extra = null) => (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1.4} alignItems="flex-start">
        <Box sx={stepBadgeSx}>{step}</Box>
        <Box>
          <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>{title}</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>{subtitle}</Typography>
        </Box>
      </Stack>
      {extra}
    </Stack>
  );

  return (
    <Box sx={{ px: { xs: 0, md: 0.5 }, pb: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: { xs: 21, md: 24 }, fontWeight: 800, color: "#0f172a" }}>
          Report Scheduler
        </Typography>
        <Typography sx={{ mt: 0.35, color: "#64748b", fontSize: 13 }}>
          Create, monitor and run automated report deliveries.
        </Typography>
      </Box>

      {status.message ? (
        <Alert severity={status.type || "info"} sx={{ mb: 2.5, borderRadius: 3 }}>
          {status.message}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          ref={editorRef}
          elevation={0}
          sx={{ ...softCardSx, p: { xs: 1.5, md: 2.25 }, order: 2, scrollMarginTop: 18 }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "#eff6ff",
                color: "#2563eb",
              }}
            >
              <CalendarMonthRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                {form.id ? "Edit Schedule" : "New Schedule"}
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>
                Configure delivery, recipients and report content.
              </Typography>
            </Box>
          </Stack>

          {metaLoading ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2.25}>
              <Box>
                {renderStepHeader(1, "Basics", "Name your schedule and select the device")}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 1.8,
                  }}
                >
                  <TextField
                    label="Schedule Name"
                    size="small"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    fullWidth
                  />

                  <SearchableDeviceSelect
                    multiple
                    label="Select Device (Optional)"
                    options={devices}
                    value={devices.filter((device) =>
                      toArray(form.deviceIds).includes(String(device?._id))
                    )}
                    onChange={(nextDevices) =>
                      setField(
                        "deviceIds",
                        (Array.isArray(nextDevices) ? nextDevices : []).map((device) => String(device?._id))
                      )
                    }
                    helperText="Search by vehicle name, IMEI, or plate number"
                  />
                </Box>
              </Box>

              <Box>
                {renderStepHeader(2, "Email Recipients", "Add up to 5 email addresses")}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(5, minmax(0, 1fr))" },
                    gap: 1.4,
                  }}
                >
                  {form.emails.map((email, index) => (
                    <TextField
                      key={index}
                      label={`Email ${index + 1}`}
                      size="small"
                      value={email}
                      onChange={(e) => setEmailAt(index, e.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MailOutlineRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Box>
                {renderStepHeader(
                  3,
                  "Schedule Settings",
                  "Choose format, timing and frequency"
                )}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                    gap: 1.6,
                  }}
                >
                  <FormControl size="small" fullWidth>
                    <InputLabel>Format</InputLabel>
                    <Select
                      label="Format"
                      value={form.format}
                      onChange={(e) => setField("format", e.target.value)}
                    >
                      {formats.map((format) => (
                        <MenuItem key={format} value={format}>
                          {String(format).toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      label="Date Range"
                      value={form.rangePreset}
                      onChange={(e) => setField("rangePreset", e.target.value)}
                    >
                      {rangePresets.map((preset) => (
                        <MenuItem key={preset} value={preset}>
                          {String(preset).replaceAll("_", " ").toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      label="Frequency"
                      value={form.frequency}
                      onChange={(e) => setField("frequency", e.target.value)}
                    >
                      {frequencies.map((frequency) => (
                        <MenuItem key={frequency} value={frequency}>
                          {String(frequency).toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    label="Time"
                    type="time"
                    value={form.timeOfDay}
                    onChange={(e) => setField("timeOfDay", e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    size="small"
                    label="Timezone"
                    value={form.timezone}
                    onChange={(e) => setField("timezone", e.target.value)}
                    fullWidth
                  />

                  {form.frequency === "weekly" ? (
                    <FormControl size="small" fullWidth>
                      <InputLabel>Weekly Day</InputLabel>
                      <Select
                        label="Weekly Day"
                        value={form.dayOfWeek}
                        onChange={(e) => setField("dayOfWeek", Number(e.target.value))}
                      >
                        {dayOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}

                  {form.frequency === "monthly" ? (
                    <TextField
                      size="small"
                      type="number"
                      label="Monthly Day"
                      value={form.dayOfMonth}
                      onChange={(e) => setField("dayOfMonth", Number(e.target.value))}
                      inputProps={{ min: 1, max: 31 }}
                      fullWidth
                    />
                  ) : null}
                </Box>
              </Box>

              <Box>
                {renderStepHeader(
                  4,
                  "Select Reports",
                  "Choose the reports you want to schedule",
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#4f46e5" }}>
                    {form.reportTypes.length} selected
                  </Typography>
                )}
                <TextField
                  size="small"
                  placeholder="Search reports"
                  value={reportSearch}
                  onChange={(e) => {
                    setReportSearch(e.target.value);
                    setVisibleReportCount(REPORT_BATCH_SIZE);
                  }}
                  fullWidth
                  sx={{ mb: 1.6 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                    gap: 1.2,
                  }}
                >
                  {visibleReports.map((report, index) => {
                    const checked = form.reportTypes.includes(report.value);
                    const tone = toneList[index % toneList.length];
                    return (
                      <Box
                        key={report.value}
                        onClick={() => {
                          const nextTypes = checked
                            ? form.reportTypes.filter((value) => value !== report.value)
                            : [...form.reportTypes, report.value];
                          setField("reportTypes", nextTypes);
                        }}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 2.5,
                          border: checked
                            ? `1px solid ${alpha(tone, 0.38)}`
                            : "1px solid rgba(226,232,240,0.95)",
                          background: checked ? alpha(tone, 0.08) : "#fff",
                          boxShadow: checked
                            ? `0 16px 34px ${alpha(tone, 0.14)}`
                            : "0 8px 24px rgba(15,23,42,0.04)",
                          px: 1.3,
                          py: 1.2,
                          transition: "all 0.18s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            borderColor: alpha(tone, 0.35),
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1.2} alignItems="flex-start">
                          <Checkbox
                            checked={checked}
                            tabIndex={-1}
                            sx={{
                              p: 0.1,
                              color: alpha(tone, 0.7),
                              "&.Mui-checked": { color: tone },
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 1.8,
                                  display: "grid",
                                  placeItems: "center",
                                  bgcolor: alpha(tone, 0.12),
                                  color: tone,
                                  flexShrink: 0,
                                }}
                              >
                                <DescriptionRoundedIcon sx={{ fontSize: 16 }} />
                              </Box>
                              <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                                {report.label}
                              </Typography>
                            </Stack>
                            <Typography sx={{ mt: 0.5, fontSize: 11.5, color: "#64748b" }}>
                              {String(report.value).replaceAll("_", " ")}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>

                {hasMoreReports ? (
                  <Stack direction="row" justifyContent="center" sx={{ mt: 1.8 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddRoundedIcon />}
                      onClick={() => setVisibleReportCount((prev) => prev + REPORT_BATCH_SIZE)}
                      sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}
                    >
                      Load More Reports
                    </Button>
                  </Stack>
                ) : null}
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 0.2,
                  py: 0.6,
                  borderTop: "1px solid rgba(226,232,240,0.9)",
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>Active Schedule</Typography>
                  <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
                    Enable to activate this schedule
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(form.isActive)}
                  onChange={(e) => setField("isActive", e.target.checked)}
                />
              </Stack>

              <Stack direction="row" spacing={1.2} flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={busy}
                  startIcon={<DescriptionRoundedIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    minWidth: 162,
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 800,
                    background: "linear-gradient(135deg, #4f46e5, #4338ca)",
                    boxShadow: "0 14px 28px rgba(79,70,229,0.22)",
                  }}
                >
                  {busy ? "Saving..." : "Save Schedule"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  sx={{ borderRadius: 3, textTransform: "none", fontWeight: 700 }}
                >
                  Reset
                </Button>
                <IconButton
                  onClick={() => {
                    refetchMeta();
                    refetchSchedules();
                  }}
                  disabled={metaFetching || schedulesFetching}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(203,213,225,0.92)",
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Paper>

        <Paper elevation={0} sx={{ ...softCardSx, p: { xs: 1.5, md: 2 }, order: 1 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.8}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                Scheduled Reports
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>
                {schedules.length} {schedules.length === 1 ? "schedule" : "schedules"} configured
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleNewSchedule}
                sx={{
                  minHeight: 36,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 12.5,
                  fontWeight: 700,
                  bgcolor: "#2563eb",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#1d4ed8", boxShadow: "none" },
                }}
                >
                New Schedule
              </Button>
            </Stack>
          </Stack>

          {schedulesLoading ? (
            <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : schedules.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: 13 }}>
              No report schedules have been created yet.
            </Alert>
          ) : (
            <Stack spacing={2}>
              <TableContainer
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  overflowX: "auto",
                }}
              >
                <Table size="small" sx={{ minWidth: 1120 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      {["Schedule", "Reports", "Recipients", "Frequency", "Next Run", "Last Run", "Status"].map((label) => (
                        <TableCell key={label} sx={{ py: 1.2, fontSize: 11.5, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.35 }}>
                          {label}
                        </TableCell>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          position: "sticky",
                          right: 0,
                          zIndex: 2,
                          py: 1.2,
                          bgcolor: "#f8fafc",
                          boxShadow: "-8px 0 14px rgba(15,23,42,0.035)",
                          fontSize: 11.5,
                          fontWeight: 800,
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: 0.35,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schedules.map((schedule, index) => {
                      const tone = toneList[index % toneList.length];
                      const emails = Array.isArray(schedule.emails) ? schedule.emails : [];
                      const reports = Array.isArray(schedule.reportTypes) ? schedule.reportTypes : [];
                      const statusTone =
                        schedule.lastRunStatus === "success"
                          ? "success"
                          : schedule.lastRunStatus === "partial"
                            ? "warning"
                            : schedule.lastRunStatus === "failed"
                              ? "error"
                              : "default";

                      return (
                        <TableRow key={schedule._id} hover sx={{ "& td": { py: 1.25, borderColor: "#eef2f7" } }}>
                          <TableCell sx={{ minWidth: 220 }}>
                            <Stack spacing={0.8}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 999,
                                    bgcolor: tone,
                                  }}
                                />
                                <Typography sx={{ fontWeight: 750, fontSize: 13, color: "#0f172a" }}>
                                  {schedule.name}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={schedule.isActive !== false ? "Active" : "Inactive"}
                                  sx={{
                                    bgcolor:
                                      schedule.isActive !== false ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.14)",
                                    color: schedule.isActive !== false ? "#16a34a" : "#64748b",
                                    fontWeight: 700,
                                  }}
                                />
                              </Stack>
                              <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                                {reports.length} reports • {emails.length} emails
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                              {reports.map((type, reportIndex) => (
                                <Chip
                                  key={type}
                                  size="small"
                                  icon={<DescriptionRoundedIcon sx={{ fontSize: 15 }} />}
                                  label={String(type).replaceAll("_", " ")}
                                  sx={{
                                    height: 24,
                                    fontSize: 10.5,
                                    bgcolor: alpha(toneList[reportIndex % toneList.length], 0.09),
                                    color: toneList[reportIndex % toneList.length],
                                    borderRadius: 2,
                                  }}
                                />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ minWidth: 140 }}>
                            <Stack direction="row" spacing={0.7} alignItems="center" useFlexGap flexWrap="wrap">
                              {emails.slice(0, 2).map((email) => (
                                <Avatar
                                  key={email}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: 11,
                                    fontWeight: 800,
                                    bgcolor: "rgba(241,245,249,0.95)",
                                    color: "#334155",
                                  }}
                                >
                                  {String(email).slice(0, 2).toLowerCase()}
                                </Avatar>
                              ))}
                              {emails.length > 2 ? (
                                <Chip size="small" label={`+${emails.length - 2}`} sx={{ borderRadius: 999 }} />
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ minWidth: 180 }}>
                            <Stack spacing={0.5}>
                              <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: "#334155" }}>
                                {String(schedule.frequency || "daily").replace(/\b\w/g, (char) => char.toUpperCase())}
                              </Typography>
                              <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                                at {formatTime(schedule.timeOfDay)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ minWidth: 170 }}>
                            <Typography sx={{ fontWeight: 650, fontSize: 12, color: "#334155" }}>
                              {formatDateTime(schedule.nextRunAt)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 170 }}>
                            <Typography sx={{ fontWeight: 650, fontSize: 12, color: "#334155" }}>
                              {formatDateTime(schedule.lastRunAt)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 120 }}>
                            <Chip
                              size="small"
                              label={schedule.lastRunStatus || "never"}
                              color={statusTone}
                              variant="outlined"
                              sx={{ height: 24, fontSize: 10.5, textTransform: "capitalize" }}
                            />
                            {schedule.lastRunMessage ? (
                              <Typography sx={{ mt: 0.6, fontSize: 11.5, color: "#64748b" }}>
                                {schedule.lastRunMessage}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              position: "sticky",
                              right: 0,
                              zIndex: 1,
                              minWidth: 138,
                              bgcolor: "#fff",
                              boxShadow: "-8px 0 14px rgba(15,23,42,0.035)",
                            }}
                          >
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton
                                aria-label={`Run ${schedule.name || "schedule"} now`}
                                title="Run now"
                                onClick={() => handleRunNow(schedule._id)}
                                disabled={busy}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1.5,
                                  bgcolor: "rgba(34,197,94,0.1)",
                                  color: "#16a34a",
                                  border: "1px solid rgba(34,197,94,0.16)",
                                }}
                              >
                                <PlayArrowIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                              <IconButton
                                aria-label={`Edit ${schedule.name || "schedule"}`}
                                title="Edit schedule"
                                onClick={() => handleEdit(schedule)}
                                disabled={busy}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1.5,
                                  bgcolor: "rgba(59,130,246,0.1)",
                                  color: "#2563eb",
                                  border: "1px solid rgba(59,130,246,0.16)",
                                }}
                              >
                                <EditIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                              <IconButton
                                aria-label={`Delete ${schedule.name || "schedule"}`}
                                title="Delete schedule"
                                onClick={() => handleDelete(schedule._id)}
                                disabled={busy}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1.5,
                                  bgcolor: "rgba(239,68,68,0.08)",
                                  color: "#ef4444",
                                  border: "1px solid rgba(239,68,68,0.14)",
                                }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
                <Button
                  variant="text"
                  startIcon={<RefreshIcon />}
                  onClick={refetchSchedules}
                  disabled={schedulesFetching}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Refresh Schedules
                </Button>
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  Showing 1 to {schedules.length} of {schedules.length} schedules
                </Typography>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ReportScheduler;
